import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Button, Chip, Alert, Divider, CircularProgress } from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as TestIcon,
  AccountTree as PipelineIcon,
  TableChart as CreateTableIcon,
  Schema as CreateSchemaIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ConnectionList from '../components/database/ConnectionList';
import ConnectionForm from '../components/database/ConnectionForm';
import SchemaTree from '../components/database/SchemaTree';
import QueryEditor from '../components/database/QueryEditor';
import CreateTableDialog from '../components/database/CreateTableDialog';
import CreateSchemaDialog from '../components/database/CreateSchemaDialog';
import MongoBrowser from '../components/database/MongoBrowser';
import { listConnections, testConnection } from '../api/connections';
import api from '../utils/api';

const DatabaseBrowserPage = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [queryText, setQueryText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createTableOpen, setCreateTableOpen] = useState(false);
  const [createSchemaOpen, setCreateSchemaOpen] = useState(false);
  const [creatingPipeline, setCreatingPipeline] = useState(false);
  const [mongoTarget, setMongoTarget] = useState({});
  const schemaTreeRef = useRef(null);

  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listConnections();
      setConnections(data.connections || []);
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const handleSelect = (conn) => {
    setSelected(conn);
    setQueryText('');
    setTestResult(null);
    setMongoTarget({});
  };

  const handleAdd = () => {
    setEditTarget(null);
    setFormOpen(true);
  };

  const handleEdit = () => {
    setEditTarget(selected);
    setFormOpen(true);
  };

  const handleTest = async () => {
    if (!selected) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnection(selected.id);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleTableClick = (schema, table) => {
    const qualified = schema ? `"${schema}"."${table}"` : `"${table}"`;
    setQueryText(`SELECT * FROM ${qualified} LIMIT 100`);
  };

  const handleUsePipeline = async () => {
    if (!selected) return;
    setCreatingPipeline(true);
    try {
      if (isMongo) {
        const pipelineRes = await api.post('/pipelines', {
          name: `Mongo ${selected.name}`,
          description: `Pipeline created from Database Browser to read ${selected.name}`,
          status: 'draft',
        });
        await api.post(`/pipelines/${pipelineRes.data.id}/nodes`, {
          name: 'MongoDB Query', type: 'input', module_name: 'mongodb_query',
          position_x: 250, position_y: 150,
          config: {
            connection_id: selected.id,
            database: mongoTarget.database || selected.database || '',
            collection: mongoTarget.collection || '',
            mode: 'find',
          },
        });
        navigate(`/pipelines/${pipelineRes.data.id}`);
        return;
      }
      // Build a query from the current editor text or a default
      const query = queryText.trim() || 'SELECT * FROM table_name LIMIT 100';

      // Build the node config from the selected connection
      const nodeConfig = {
        db_type: selected.db_type,
        host: selected.host,
        port: selected.port,
        database: selected.database,
        username: selected.username,
        password: selected.password || '',
        query,
      };

      // Create a new pipeline
      const pipelineRes = await api.post('/pipelines', {
        name: `Query ${selected.name === '__internal__' ? 'Platform DB' : selected.name}`,
        description: `Pipeline created from Database Browser to query ${selected.database || 'database'}`,
        status: 'draft',
      });
      const pipeline = pipelineRes.data;

      // Add a Database Query node
      await api.post(`/pipelines/${pipeline.id}/nodes`, {
        name: 'Database Query',
        type: 'input',
        module_name: 'database_query',
        position_x: 250,
        position_y: 150,
        config: nodeConfig,
      });

      // Navigate to the pipeline designer
      navigate(`/pipelines/${pipeline.id}`);
    } catch (err) {
      console.error('Failed to create pipeline:', err);
      setTestResult({ success: false, message: err.response?.data?.detail || 'Failed to create pipeline' });
    } finally {
      setCreatingPipeline(false);
    }
  };

  const isInternal = selected?.name === '__internal__';
  const isMongo = selected?.db_type === 'mongodb';

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 112px)', gap: 2 }}>
      {/* Left panel — connection list */}
      <Paper sx={{ width: 280, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ConnectionList
          connections={connections}
          selectedId={selected?.id}
          onSelect={handleSelect}
          onAdd={handleAdd}
        />
      </Paper>

      {/* Right panel — details, browser, query */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
        {!selected ? (
          <Paper sx={{ p: 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Select a connection to start browsing</Typography>
          </Paper>
        ) : (
          <>
            {/* Connection details bar */}
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">{isInternal ? 'Platform DB' : selected.name}</Typography>
              <Chip label={selected.db_type} size="small" />
              {!isInternal && !isMongo && <Typography variant="body2" color="text.secondary">{selected.host}:{selected.port}/{selected.database}</Typography>}
              {isMongo && !selected.connection_string && <Typography variant="body2" color="text.secondary">{selected.host}:{selected.port}/{selected.database}</Typography>}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={handleTest} disabled={testing} startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}>
                Test
              </Button>
              {!isInternal && (
                <Button size="small" onClick={handleEdit} startIcon={<EditIcon />}>Edit</Button>
              )}
              {!isMongo && (
                <>
                  <Button size="small" onClick={() => setCreateSchemaOpen(true)} startIcon={<CreateSchemaIcon />}>
                    Create Schema
                  </Button>
                  <Button size="small" onClick={() => setCreateTableOpen(true)} startIcon={<CreateTableIcon />}>
                    Create Table
                  </Button>
                </>
              )}
              <Button
                size="small"
                onClick={handleUsePipeline}
                disabled={creatingPipeline}
                startIcon={creatingPipeline ? <CircularProgress size={16} /> : <PipelineIcon />}
              >
                Use in Pipeline
              </Button>
            </Paper>

            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} onClose={() => setTestResult(null)}>
                {testResult.message}
              </Alert>
            )}

            {isMongo ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Collection Browser</Typography>
                <Divider sx={{ mb: 1 }} />
                <MongoBrowser
                  key={selected.id}
                  connection={selected}
                  onSelectCollection={(database, collection) => setMongoTarget({ database, collection })}
                />
              </Paper>
            ) : (
            <>
            {/* Schema browser */}
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" gutterBottom>Schema Browser</Typography>
                <Typography variant="caption" color="text.secondary">Right-click to drop schema or table</Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <SchemaTree ref={schemaTreeRef} connectionId={selected.id} onTableClick={handleTableClick} />
            </Paper>

            {/* Query editor */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Query Editor</Typography>
              <Divider sx={{ mb: 1 }} />
              <QueryEditor connectionId={selected.id} initialQuery={queryText} />
            </Paper>
            </>
            )}
          </>
        )}
      </Box>

      {/* Connection form dialog */}
      <ConnectionForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchConnections}
        editConnection={editTarget}
      />

      {/* Create schema dialog */}
      {selected && (
        <CreateSchemaDialog
          open={createSchemaOpen}
          onClose={() => setCreateSchemaOpen(false)}
          connectionId={selected.id}
          onCreated={() => schemaTreeRef.current?.refresh()}
        />
      )}

      {/* Create table dialog */}
      {selected && (
        <CreateTableDialog
          open={createTableOpen}
          onClose={() => setCreateTableOpen(false)}
          connectionId={selected.id}
          schemas={schemaTreeRef.current?.schemas}
          onCreated={() => schemaTreeRef.current?.refresh()}
        />
      )}
    </Box>
  );
};

export default DatabaseBrowserPage;
